import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import AdminLayout from "@/components/feature/AdminLayout";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import AdminHeader from "@/components/feature/AdminHeader";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
// VPS Storage — import ready for future upload integration
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { STORAGE_CONFIG, isVpsStorageReady } from "@/lib/storageConfig";
import MediaPackageModal, {
  type MediaPackage, type SimpleClient, type SimpleJob,
  PACKAGE_TYPES,
} from "./components/MediaPackageModal";
import ModuleGate from "@/components/base/ModuleGate";
import { deleteFileFromVps } from "@/lib/vpsStorage";

const TYPE_STYLES: Record<string, string> = {
  online_dvd:  "bg-violet-400/15 text-violet-400",
  usb:         "bg-amber-400/15 text-amber-400",
  embed:       "bg-sky-400/15 text-sky-400",
  cover_frame: "bg-emerald-400/15 text-emerald-400",
  combo:       "bg-[#C8A96E]/15 text-[#C8A96E]",
};

export default function AdminMediaPackages() {
  const navigate = useNavigate();
  const [tenantId, setTenantId]     = useState<string | null>(null);
  const [packages, setPackages]     = useState<MediaPackage[]>([]);
  const [clients, setClients]       = useState<SimpleClient[]>([]);
  const [jobs, setJobs]             = useState<SimpleJob[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [search, setSearch]         = useState("");
  const [selected, setSelected]     = useState<MediaPackage | null>(null);
  const [modalOpen, setModalOpen]   = useState(false);
  const [isNew, setIsNew]           = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchAll = useCallback(async (tid: string) => {
    setLoading(true);
    setError(null);
    try {
      const [pkgRes, clientRes, jobRes] = await Promise.all([
        supabase.from("media_packages").select("*").eq("tenant_id", tid).order("created_at", { ascending: false }),
        supabase.from("clients").select("id, full_name").eq("tenant_id", tid).eq("status", "active").order("full_name"),
        supabase.from("jobs").select("id, title, client_id").eq("tenant_id", tid).order("title"),
      ]);
      if (pkgRes.error) throw new Error(pkgRes.error.message);
      if (pkgRes.data)    setPackages(pkgRes.data as MediaPackage[]);
      if (clientRes.data) setClients(clientRes.data as SimpleClient[]);
      if (jobRes.data)    setJobs(jobRes.data as SimpleJob[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load media packages.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchAll(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchAll]);

  const clientName = (id: string) => clients.find((c) => c.id === id)?.full_name ?? "—";

  const filtered = packages.filter((p) =>
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    clientName(p.client_id).toLowerCase().includes(search.toLowerCase()),
  );

  const handleNew = () => { setSelected(null); setIsNew(true); setModalOpen(true); };

  const handleEdit = (p: MediaPackage) => { setSelected(p); setIsNew(false); setModalOpen(true); };

  const handleTogglePublish = async (pkg: MediaPackage) => {
    setTogglingId(pkg.id);
    const { data, error } = await supabase
      .from("media_packages")
      .update({ is_published: !pkg.is_published, updated_at: new Date().toISOString() })
      .eq("id", pkg.id).eq("tenant_id", tenantId!).select().maybeSingle();
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error" });
    } else if (data) {
      setPackages((prev) => prev.map((p) => (p.id === pkg.id ? (data as MediaPackage) : p)));
      Swal.fire({
        title: (data as MediaPackage).is_published ? "Published!" : "Unpublished",
        text: (data as MediaPackage).is_published ? `Live at /media/${pkg.public_token}` : "Package is now private.",
        icon: "success", timer: 2000, showConfirmButton: false,
      });
    }
    setTogglingId(null);
  };

  const handleDelete = async (pkg: MediaPackage) => {
    const result = await Swal.fire({
      title: "Delete Package?", text: `"${pkg.title}" will be permanently removed.`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Yes, delete", cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444", cancelButtonColor: "#374151",
    });
    if (!result.isConfirmed || !tenantId) return;

    // Cleanup uploaded files from VPS
    const urlsToDelete: string[] = [];
    if (pkg.cover_url) urlsToDelete.push(pkg.cover_url);
    if (pkg.frame_url) urlsToDelete.push(pkg.frame_url);
    if (pkg.video_url) urlsToDelete.push(pkg.video_url);
    if (pkg.download_url) urlsToDelete.push(pkg.download_url);

    const { data: fileRecords } = await supabase
      .from("uploaded_files")
      .select("id, file_url")
      .in("file_url", urlsToDelete)
      .eq("tenant_id", tenantId);

    if (fileRecords && fileRecords.length > 0) {
      await Promise.all(
        fileRecords.map(async (rec) => {
          try {
            await deleteFileFromVps({ fileId: rec.id, tenantId, silent: true });
          } catch {
            // ignore
          }
        })
      );
    }

    const { error } = await supabase.from("media_packages").delete().eq("id", pkg.id).eq("tenant_id", tenantId);
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error" });
    } else {
      setPackages((prev) => prev.filter((p) => p.id !== pkg.id));
      Swal.fire({ title: "Deleted", text: "Package removed.", icon: "success", timer: 1500, showConfirmButton: false });
    }
  };

  const handleSave = async (data: MediaPackage, uploadMeta?: Record<string, { fileId?: string }>) => {
    if (!tenantId) return;
    if (isNew) {
      const { id: _id, created_at: _ca, updated_at: _ua, ...rest } = data;
      const payload = { ...rest, tenant_id: tenantId, is_published: false,
        cover_url: rest.cover_url || null, frame_url: rest.frame_url || null,
        video_url: rest.video_url || null, download_url: rest.download_url || null,
        usb_label: rest.usb_label || null, notes: rest.notes || null,
      };
      const { data: created, error } = await supabase.from("media_packages").insert(payload).select().maybeSingle();
      if (error) {
        // Cleanup uploaded files if DB insert failed
        if (uploadMeta) {
          await Promise.all(
            Object.values(uploadMeta).map(async (meta) => {
              if (meta.fileId) {
                try {
                  await deleteFileFromVps({ fileId: meta.fileId, tenantId, silent: true });
                } catch {
                  // ignore cleanup failure
                }
              }
            })
          );
        }
        Swal.fire({ title: "Error", text: error.message, icon: "error" });
        return;
      }
      if (created) setPackages((prev) => [created as MediaPackage, ...prev]);
      Swal.fire({ title: "Created!", text: "Package created. Publish when ready.", icon: "success", timer: 1800, showConfirmButton: false });
    } else {
      const { data: updated, error } = await supabase.from("media_packages")
        .update({ job_id: data.job_id, client_id: data.client_id, package_type: data.package_type,
          title: data.title, cover_url: data.cover_url || null, frame_url: data.frame_url || null,
          video_url: data.video_url || null, download_url: data.download_url || null,
          usb_label: data.usb_label || null, notes: data.notes || null, updated_at: new Date().toISOString(),
        })
        .eq("id", data.id).eq("tenant_id", tenantId).select().maybeSingle();
      if (error) {
        Swal.fire({ title: "Error", text: error.message, icon: "error" });
        return;
      }
      if (updated) setPackages((prev) => prev.map((p) => (p.id === (updated as MediaPackage).id ? (updated as MediaPackage) : p)));
      Swal.fire({ title: "Saved", text: "Package updated.", icon: "success", timer: 1500, showConfirmButton: false });
    }
    setModalOpen(false);
  };

  const copyLink = (token: string) => {
    const url = `${window.location.origin}/media/${token}`;
    navigator.clipboard.writeText(url);
    Swal.fire({ title: "Copied!", text: url, icon: "success", timer: 1500, showConfirmButton: false });
  };

  return (
    <AdminLayout>
      <AdminHeader
        title="Media Packages"
        subtitle="DVD online, USB, embed and frame deliveries"
        actions={
          <button onClick={handleNew} className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
            New Package
          </button>
        }
      />

      <ModuleGate moduleId="media_packages">
      <div className="px-4 md:px-8 py-4 md:py-7">
        {/* Search */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search packages, clients…"
              className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-4 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#2563eb]/50 transition-colors" />
          </div>
          <span className="text-slate-400 text-sm ml-auto">{filtered.length} results</span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState message={error} onRetry={() => tenantId && fetchAll(tenantId)} showLogin />
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 max-w-xs mx-auto text-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-blue-50">
                <i className="ri-disc-line text-[#2563eb] text-2xl"></i>
              </div>
              <div>
                <h3 className="text-[#0f172a] font-medium text-sm mb-1">
                  {packages.length === 0 ? "No packages yet" : "No packages found"}
                </h3>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {packages.length === 0
                    ? "Create a media package to deliver online DVDs, USBs, or embedded videos to your clients."
                    : "Try adjusting your search term."}
                </p>
              </div>
              {packages.length === 0 && (
                <button
                  onClick={handleNew}
                  className="flex items-center gap-2 bg-[#2563eb] hover:bg-blue-700 text-black text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  Create First Package
                </button>
              )}
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Package</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Client</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Type</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Status</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Public Link</th>
                  <th className="px-5 py-3.5 text-right text-slate-400 text-xs font-medium uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((pkg) => {
                  const typeInfo = PACKAGE_TYPES.find((t) => t.key === pkg.package_type);
                  const isToggling = togglingId === pkg.id;
                  return (
                    <tr key={pkg.id} className="hover:bg-slate-50 transition-colors">
                      {/* Cover + title */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 shrink-0 flex items-center justify-center">
                            {pkg.cover_url ? (
                              <img src={pkg.cover_url} alt={pkg.title} className="w-full h-full object-cover object-top"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                            ) : (
                              <span className={`w-5 h-5 flex items-center justify-center ${TYPE_STYLES[pkg.package_type] ?? "text-slate-300"}`}>
                                <i className={`${typeInfo?.icon ?? "ri-disc-line"} text-base`}></i>
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-[#0f172a] text-sm font-medium truncate max-w-[180px]">{pkg.title}</p>
                            <p className="text-slate-400 text-[10px] font-mono truncate max-w-[180px]">{pkg.public_token.slice(0, 12)}…</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-slate-500 text-sm">{clientName(pkg.client_id)}</p>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${TYPE_STYLES[pkg.package_type] ?? "bg-slate-100 text-slate-400"}`}>
                          {typeInfo?.label ?? pkg.package_type}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${pkg.is_published ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                          {pkg.is_published ? "Live" : "Draft"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        {pkg.is_published ? (
                          <button onClick={() => copyLink(pkg.public_token)}
                            className="flex items-center gap-1.5 text-xs text-[#2563eb] hover:text-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                            <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-links-line text-xs"></i></span>
                            Copy link
                          </button>
                        ) : (
                          <span className="text-slate-300 text-xs">Not published</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          {/* Publish toggle */}
                          <button onClick={() => handleTogglePublish(pkg)} disabled={isToggling}
                            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all cursor-pointer disabled:opacity-50 ${
                              pkg.is_published ? "text-slate-400 hover:text-amber-500 hover:bg-amber-50" : "text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50"
                            }`} title={pkg.is_published ? "Unpublish" : "Publish"}>
                            {isToggling ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                              : <i className={`text-sm ${pkg.is_published ? "ri-eye-off-line" : "ri-send-plane-line"}`}></i>}
                          </button>
                          <button onClick={() => handleEdit(pkg)}
                            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer" title="Edit">
                            <i className="ri-pencil-line text-sm"></i>
                          </button>
                          <button onClick={() => handleDelete(pkg)}
                            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer" title="Delete">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modalOpen && (
        <MediaPackageModal
          pkg={selected} isNew={isNew} clients={clients} jobs={jobs}
          tenantId={tenantId || ""}
          onClose={() => setModalOpen(false)} onSave={handleSave}
        />
      )}
      </ModuleGate>
    </AdminLayout>
  );
}
